import apiClient from './apiClient';

export interface Friend {
  id: number;
  friend_user_id: string | number; // UUID in Supabase, number in legacy
  first_name: string;
  last_name: string;
  email: string;
  avatar_url?: string;
  friend_count: number;
  status: 'pending' | 'accepted' | 'blocked';
  created_at: string;
  requested_at?: string;
  responded_at?: string;
}

export interface FriendRequest {
  id: number;
  sender_id: number;
  recipient_id: number;
  sender_first_name: string;
  sender_last_name: string;
  sender_email: string;
  sender_avatar?: string;
  recipient_first_name?: string;
  recipient_last_name?: string;
  recipient_email?: string;
  recipient_avatar?: string;
  message?: string;
  status: 'pending' | 'accepted' | 'declined' | 'expired';
  sent_at?: string;
  created_at?: string;
  responded_at?: string;
  expires_at?: string;
}

export interface FriendGroup {
  id: number;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  member_count?: number;
  is_owner?: boolean;
  created_at: string;
  updated_at: string;
}

export interface FriendSuggestion {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  avatar_url?: string;
  friend_count: number;
  mutual_friend_count: number;
}

export interface ActivityFeedItem {
  id: string;
  user_id: string;
  actor_id: string;
  action_type: 'meetup_created' | 'meetup_joined' | 'friend_added' | 'invitation_sent' | 'meetup_completed';
  entity_type: 'meetup' | 'friend' | 'invitation';
  entity_id: string | null;
  metadata: Record<string, unknown>;
  is_read: boolean;
  created_at: string;
  actor_first_name?: string;
  actor_last_name?: string;
  actor_avatar_url?: string;
}

export interface ActivityFeedResponse {
  success: boolean;
  data: {
    items: ActivityFeedItem[];
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface UnreadCountResponse {
  success: boolean;
  data: {
    unreadCount: number;
  };
}

export interface FavoriteFriend {
  friendship_id: number;
  is_favorite: boolean;
  friendship_created_at: string;
  friend_user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  avatar_url?: string;
}

export interface FavoritesResponse {
  success: boolean;
  data: {
    favorites: FavoriteFriend[];
    total: number;
  };
}

export interface FriendsResponse {
  success: boolean;
  data: {
    friends: Friend[];
    groups?: FriendGroup[];
    total: number;
  };
}

export interface FriendRequestsResponse {
  success: boolean;
  data: {
    requests: FriendRequest[];
    total: number;
  };
}

export interface FriendSuggestionsResponse {
  success: boolean;
  data: {
    suggestions: FriendSuggestion[];
  };
}

export const friendsApi = {
  // Friends management
  getFriends: async (status: 'all' | 'accepted' | 'pending' | 'blocked' = 'accepted') => {
    const response = await apiClient.api.get<FriendsResponse>('/v1/friends', {
      params: { status, includeGroups: true }
    });
    return response.data;
  },

  removeFriend: async (friendId: number) => {
    const response = await apiClient.api.delete(`/v1/friends/${friendId}`);
    return response.data;
  },

  // Friend requests
  getFriendRequests: async (type: 'sent' | 'received' | 'all' = 'received') => {
    const response = await apiClient.api.get<FriendRequestsResponse>('/v1/friends/requests', {
      params: { type, status: 'pending' }
    });
    return response.data;
  },

  sendFriendRequest: async (recipientId: number, message?: string) => {
    const response = await apiClient.api.post('/v1/friends/request', {
      recipientId,
      message
    });
    return response.data;
  },

  respondToFriendRequest: async (requestId: number, action: 'accept' | 'decline') => {
    const response = await apiClient.api.post(`/v1/friends/request/${requestId}/respond`, {
      action
    });
    return response.data;
  },

  // Friend suggestions
  getFriendSuggestions: async (limit: number = 10) => {
    const response = await apiClient.api.get<FriendSuggestionsResponse>('/v1/friends/suggestions', {
      params: { limit, excludeBlocked: true }
    });
    return response.data;
  },

  // Mutual friends
  getMutualFriends: async (userId: number) => {
    const response = await apiClient.api.get('/v1/friends/mutual', {
      params: { userId }
    });
    return response.data;
  },

  // Blocking
  blockUser: async (userId: number, reason?: string) => {
    const response = await apiClient.api.post('/v1/friends/block', {
      userId,
      reason
    });
    return response.data;
  },

  unblockUser: async (userId: number) => {
    const response = await apiClient.api.post('/v1/friends/unblock', {
      userId
    });
    return response.data;
  },

  // Friend groups
  getFriendGroups: async () => {
    const response = await apiClient.api.get('/v1/friends/groups');
    return response.data;
  },

  createFriendGroup: async (name: string, description?: string, color?: string, friendIds?: (string | number)[]) => {
    const response = await apiClient.api.post('/v1/friends/groups', {
      name,
      description,
      color,
      friendIds
    });
    return response.data;
  },

  updateFriendGroup: async (groupId: number, name: string, description?: string, color?: string) => {
    const response = await apiClient.api.put(`/v1/friends/groups/${groupId}`, {
      name,
      description,
      color
    });
    return response.data;
  },

  deleteFriendGroup: async (groupId: number) => {
    const response = await apiClient.api.delete(`/v1/friends/groups/${groupId}`);
    return response.data;
  },

  getGroupMembers: async (groupId: number) => {
    const response = await apiClient.api.get(`/v1/friends/groups/${groupId}/members`);
    return response.data;
  },

  addToGroup: async (friendId: number, groupId: number) => {
    const response = await apiClient.api.post('/v1/friends/groups/add-member', {
      friendId,
      groupId
    });
    return response.data;
  },

  removeFromGroup: async (friendId: number, groupId: number) => {
    const response = await apiClient.api.post('/v1/friends/groups/remove-member', {
      friendId,
      groupId
    });
    return response.data;
  },

  // Search users for friend requests
  searchUsers: async (query: string) => {
    const response = await apiClient.api.get('/v1/users/search', {
      params: { q: query }
    });
    return response.data;
  },

  // Activity feed
  getActivityFeed: async (options: { limit?: number; offset?: number; unreadOnly?: boolean } = {}) => {
    const response = await apiClient.api.get<ActivityFeedResponse>('/v1/friends/activity-feed', {
      params: {
        limit: options.limit ?? 20,
        offset: options.offset ?? 0,
        unreadOnly: options.unreadOnly ?? false,
      },
    });
    return response.data;
  },

  markActivityRead: async (activityId: string) => {
    const response = await apiClient.api.post(`/v1/friends/activity-feed/${activityId}/read`);
    return response.data;
  },

  markAllActivityRead: async () => {
    const response = await apiClient.api.post('/v1/friends/activity-feed/read-all');
    return response.data;
  },

  getUnreadCount: async () => {
    const response = await apiClient.api.get<UnreadCountResponse>('/v1/friends/activity-feed/unread-count');
    return response.data;
  },

  // Favorites
  toggleFavorite: async (friendId: string) => {
    const response = await apiClient.api.put(`/v1/friends/${friendId}/favorite`);
    return response.data;
  },

  getFavorites: async () => {
    const response = await apiClient.api.get<FavoritesResponse>('/v1/friends/favorites');
    return response.data;
  },
};
